import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2EmptyState,
  V2Modal,
  V2Tabs,
} from '@sakhtar/design-system/react';
import { systemClock } from '@sakhtar/calendar';
import { UserAvatar } from '../../lib/avatars.js';
import { useCapabilities } from '../../lib/capabilities.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { amountUnitLabel, formatRialFa } from '../../lib/finance-display.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { SalesRepProfileResponse } from '../../lib/types/commission.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { PartyLink } from './PartyLink.js';
import { YarFace } from './SalesYar.js';
import {
  ACTIVITY_TYPE_LABELS,
  OUTCOME_LABELS,
  SENTIMENT_LABELS,
  VERDICT_LABEL,
  VERDICT_TONE,
  averageBarPercent,
  faInt,
  formatDuration,
  formatInsightValue,
  formatPermille,
} from './salesRepProfileModel.js';
import {
  METHOD_LABELS,
  PERIOD_PRESETS,
  periodFor,
  type PeriodPreset,
} from './teamPerformanceModel.js';
import './salesRepProfile.css';

type TabId = 'overview' | 'calls' | 'sales' | 'activities';
const TABS: readonly { readonly id: TabId; readonly label: string }[] = [
  { id: 'overview', label: 'مرور کلی' },
  { id: 'calls', label: 'تماس‌ها' },
  { id: 'sales', label: 'فروش و پورسانت' },
  { id: 'activities', label: 'فعالیت‌ها' },
];

/**
 * پروفایل فروشنده — everything about one salesperson in one place: sales,
 * collection, commission, calls (from AI call analysis) and activities for a
 * period, set beside the rest of the team, plus «تحلیل فروش‌یار» — named
 * strengths and weaknesses from that comparison.
 *
 * All figures are server aggregates (integer Rial, exact). The analysis is a
 * deterministic comparison, not a model call: it costs no AI credit and gives
 * the same answer for the same data.
 */
export function SalesRepProfilePage() {
  const { slug, salespersonId } = useParams<{ slug: string; salespersonId: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { hasCapability } = useCapabilities();
  const { unit, label: unitLabel } = useAmountDisplay();
  const [params, setParams] = useSearchParams();
  const canTeam = hasCapability('sales.team.read');
  const preset = (params.get('period') as PeriodPreset | null) ?? 'thisMonth';
  const tab = (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'overview') as TabId;
  const [analysisOpen, setAnalysisOpen] = useState(false);

  const range = useMemo(() => periodFor(preset, systemClock.today()), [preset]);
  const profile = useApiGet<SalesRepProfileResponse>(
    slug && salespersonId && canTeam
      ? `${base}/api/v1/sales/commission/team/${encodeURIComponent(salespersonId)}/profile?from=${range.from}&to=${range.to}`
      : null,
    [slug, salespersonId, range.from, range.to, canTeam],
  );

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const money = (digits: string) => `${formatRialFa(digits, unit)} ${unitLabel}`;
  const rialText = (digits: string) => formatRialFa(digits, unit);

  if (!canTeam) {
    return (
      <StatePanel
        variant="permission_denied"
        title="برای دیدن پروفایل فروشنده دسترسی ندارید"
        description="این صفحه به دسترسی مشاهدهٔ عملکرد تیم فروش نیاز دارد."
      />
    );
  }
  if (profile.status === 'loading') return <LoadingState />;
  if (profile.status === 'error') {
    return (
      <StatePanel
        variant={profile.error.status === 404 ? 'not_found' : 'network_error'}
        title={profile.error.status === 404 ? 'فروشنده پیدا نشد' : 'ارتباط با سامانه برقرار نشد'}
        description={
          profile.error.status === 404
            ? 'این فروشنده در سازمان شما وجود ندارد.'
            : 'لطفاً دوباره تلاش کنید.'
        }
        action={
          <Link className="sk-btn sk-btn--secondary" to={`${base}/crm/team`}>
            بازگشت به عملکرد تیم
          </Link>
        }
      />
    );
  }

  const data = profile.data;
  const { member, insights, calls, activities, commission } = data;
  const cur = member.current;
  const growth = insights.salesGrowthPercent;
  const answerRate =
    calls && calls.total > 0 ? Math.round((calls.answered * 1000) / calls.total) : null;

  return (
    <main className="rep-profile" data-module="crm">
      <Link className="rep-profile__back" to={`${base}/crm/team`}>
        ← عملکرد تیم فروش
      </Link>

      <header className="rep-profile__head">
        <span className="rep-profile__who">
          <UserAvatar
            name={member.name}
            source={{
              avatarPreset: (member.avatarPreset as never) ?? null,
              avatarPhotoDataUrl: null,
            }}
            size={56}
          />
          <span>
            <h1>{member.name}</h1>
            <small>
              {member.active ? 'فروشندهٔ فعال' : 'غیرفعال'}
              {member.plan
                ? ` · پورسانت: ${METHOD_LABELS[member.plan.method]}`
                : ' · بدون طرح پورسانت'}
            </small>
          </span>
        </span>
        <V2Button
          variant="primary"
          className="call-base__yar-btn"
          onClick={() => setAnalysisOpen(true)}
        >
          <YarFace />
          تحلیل فروش‌یار
        </V2Button>
      </header>

      <div className="rep-profile__period" role="group" aria-label="بازهٔ زمانی">
        {PERIOD_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`rep-chip${preset === p.id ? ' is-active' : ''}`}
            onClick={() => update({ period: p.id === 'thisMonth' ? null : p.id })}
          >
            {p.label}
          </button>
        ))}
        <small>
          {displayJalaliDate(data.period.from)} تا {displayJalaliDate(data.period.to)}
        </small>
      </div>

      <div className="rep-stats" role="group" aria-label="خلاصهٔ عملکرد">
        <Stat
          label="فروش"
          value={money(cur.salesRial)}
          sub={
            growth === null
              ? `${faInt(cur.invoiceCount)} فاکتور`
              : `${growth >= 0 ? '▲' : '▼'} ${faInt(Math.abs(growth))}٪ نسبت به دورهٔ قبل`
          }
          tone={growth !== null && growth < 0 ? 'down' : growth !== null ? 'up' : undefined}
        />
        <Stat
          label="وصول‌شده"
          value={money(cur.collectedRial)}
          sub={`${faInt(cur.ordersCount)} سفارش`}
        />
        <Stat
          label="پورسانت"
          value={money(cur.commissionRial)}
          sub={`آزادشده ${money(cur.commissionReleasedRial)}`}
        />
        <Stat
          label="تماس‌ها"
          value={faInt(calls?.total ?? 0)}
          sub={answerRate === null ? 'تماسی ثبت نشده' : `پاسخ ${formatPermille(answerRate)}`}
        />
        <Stat
          label="فعالیت"
          value={faInt(cur.activitiesDone)}
          sub={`باز ${faInt(activities.open)} · عقب‌افتاده ${faInt(activities.overdue)}`}
          tone={activities.overdue > 0 ? 'down' : undefined}
        />
        <Stat
          label="خط لولهٔ باز"
          value={faInt(member.pipelineOpenCount)}
          sub={`${money(member.pipelineRial)}`}
        />
      </div>

      <V2Tabs
        items={TABS.map((t) => ({ id: t.id, label: t.label }))}
        activeId={tab}
        onChange={(id) => update({ tab: id === 'overview' ? null : id })}
      />

      {tab === 'overview' ? (
        <V2Card>
          <div className="rep-section">
            <h2>مقایسه با تیم</h2>
            {!insights.comparable ? (
              <V2Alert tone="info" title="مقایسه با تیم ممکن نیست">
                {insights.notes[0]}
              </V2Alert>
            ) : null}
            <div className="rep-table-wrap">
              <table className="rep-table">
                <thead>
                  <tr>
                    <th>شاخص</th>
                    <th>این فروشنده</th>
                    <th>میانگین تیم</th>
                    <th>رتبه</th>
                    <th>نسبت به میانگین</th>
                    <th>ارزیابی</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.comparisons.map((c) => (
                    <tr key={c.id}>
                      <th scope="row">{c.label}</th>
                      <td className="sk-num">{formatInsightValue(c.value, c.unit, rialText)}</td>
                      <td className="sk-num">
                        {formatInsightValue(c.teamAverage, c.unit, rialText)}
                      </td>
                      <td className="sk-num">
                        {c.rank === null ? '—' : `${faInt(c.rank)} از ${faInt(c.of)}`}
                      </td>
                      <td>
                        {c.percentOfAverage === null ? (
                          '—'
                        ) : (
                          <span className="rep-bar" title={`${faInt(c.percentOfAverage)}٪ میانگین`}>
                            <span
                              className={`rep-bar__fill is-${c.verdict}`}
                              style={{ width: `${averageBarPercent(c.percentOfAverage)}%` }}
                            />
                            <span className="rep-bar__mid" aria-hidden="true" />
                          </span>
                        )}
                      </td>
                      <td>
                        <V2Badge tone={VERDICT_TONE[c.verdict]}>{VERDICT_LABEL[c.verdict]}</V2Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </V2Card>
      ) : null}

      {tab === 'calls' ? (
        <V2Card>
          <div className="rep-section">
            <div className="rep-section__row">
              <h2>تماس‌ها</h2>
              <Link
                className="sk-btn sk-btn--secondary sk-btn--sm"
                to={`${base}/crm/call-base?operator=${encodeURIComponent(member.salespersonId)}`}
              >
                مشاهدهٔ تماس‌ها در پایگاه تماس
              </Link>
            </div>
            {!calls || calls.total === 0 ? (
              <V2EmptyState
                icon="phone"
                title="در این بازه تماسی ثبت نشده"
                description="تماس‌های این فروشنده پس از ثبت یا بارگذاری در پایگاه تماس اینجا خلاصه می‌شود."
                compact
              />
            ) : (
              <>
                <dl className="rep-facts">
                  <Fact label="کل تماس‌ها" value={faInt(calls.total)} />
                  <Fact
                    label="ورودی / خروجی"
                    value={`${faInt(calls.inbound)} / ${faInt(calls.outbound)}`}
                  />
                  <Fact label="پاسخ‌داده‌شده" value={faInt(calls.answered)} />
                  <Fact label="بی‌پاسخ" value={faInt(calls.missed)} />
                  <Fact
                    label="نرخ پاسخگویی"
                    value={answerRate === null ? '—' : formatPermille(answerRate)}
                  />
                  <Fact
                    label="میانگین مکالمه"
                    value={formatDuration(calls.averageDurationSeconds)}
                  />
                  <Fact label="تحلیل‌شده با فروش‌یار" value={faInt(calls.analyzed)} />
                  <Fact label="پرریسک" value={faInt(calls.highRisk)} />
                </dl>
                {calls.analyzed > 0 ? (
                  <div className="rep-dist">
                    <Distribution
                      title="نتیجهٔ تماس‌های تحلیل‌شده"
                      total={calls.analyzed}
                      items={Object.entries(calls.outcomes).map(([k, v]) => ({
                        label: OUTCOME_LABELS[k] ?? k,
                        value: v,
                      }))}
                    />
                    <Distribution
                      title="احساس مشتری"
                      total={calls.analyzed}
                      items={Object.entries(calls.sentiment).map(([k, v]) => ({
                        label: SENTIMENT_LABELS[k] ?? k,
                        value: v,
                      }))}
                    />
                  </div>
                ) : (
                  <V2Alert tone="info" title="هنوز تماسی تحلیل نشده است">
                    نتیجه، احساس مشتری و ریسک فقط برای تماس‌هایی نمایش داده می‌شود که با «تحلیل
                    فروش‌یار» تحلیل شده باشند.
                  </V2Alert>
                )}
              </>
            )}
          </div>
        </V2Card>
      ) : null}

      {tab === 'sales' ? (
        <V2Card>
          <div className="rep-section">
            <h2>فروش و پورسانت</h2>
            <dl className="rep-facts">
              <Fact label="فروش (مبنای مالیاتی)" value={money(cur.salesRial)} />
              <Fact label="وصول‌شده" value={money(cur.collectedRial)} />
              <Fact label="پورسانت" value={money(cur.commissionRial)} />
              <Fact label="پورسانت آزادشده" value={money(cur.commissionReleasedRial)} />
              <Fact label="سفارش‌ها" value={faInt(cur.ordersCount)} />
              <Fact
                label="پیش‌فاکتور (پذیرفته)"
                value={`${faInt(cur.quotesCount)} (${faInt(cur.quotesAccepted)})`}
              />
              <Fact
                label="سرنخ برده / ازدست‌رفته"
                value={`${faInt(cur.leadsWon)} / ${faInt(cur.leadsLost)}`}
              />
              <Fact
                label="سهم از فروش تیم"
                value={shareText(cur.salesRial, data.teamTotals.salesRial)}
              />
            </dl>
            {commission.invoices === null ? (
              <V2Alert tone="info" title="ریز فاکتورها در دسترس نیست">
                برای دیدن فاکتورهای مبنای پورسانت، دسترسی «مشاهدهٔ پورسانت» لازم است.
              </V2Alert>
            ) : commission.invoices.length === 0 ? (
              <V2EmptyState
                icon="invoice"
                title="در این بازه فاکتوری ثبت نشده"
                description="فاکتورهای صادرشده با پورسانت این فروشنده اینجا فهرست می‌شوند."
                compact
              />
            ) : (
              <div className="rep-table-wrap">
                <table className="rep-table">
                  <thead>
                    <tr>
                      <th>فاکتور</th>
                      <th>تاریخ</th>
                      <th>مشتری</th>
                      <th>مبنا ({amountUnitLabel(unit)})</th>
                      <th>پورسانت</th>
                      <th>آزادشده</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commission.invoices.map((inv) => (
                      <tr key={inv.invoiceId}>
                        <th scope="row">
                          <Link to={`${base}/crm/invoices/${inv.invoiceId}`} className="ltr">
                            {inv.officialNumber ?? inv.orderRef}
                          </Link>
                          {inv.status === 'reversed' ? <V2Badge tone="warn">برگشتی</V2Badge> : null}
                        </th>
                        <td>{displayJalaliDate(inv.businessDate)}</td>
                        <td>
                          <PartyLink base={base} partyRef={inv.customerRef}>
                            {inv.customerName}
                          </PartyLink>
                        </td>
                        <td className="sk-num">{rialText(inv.baseRial)}</td>
                        <td className="sk-num">{rialText(inv.commissionRial)}</td>
                        <td className="sk-num">{rialText(inv.releasedRial)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </V2Card>
      ) : null}

      {tab === 'activities' ? (
        <V2Card>
          <div className="rep-section">
            <div className="rep-section__row">
              <h2>فعالیت‌ها</h2>
              <Link className="sk-btn sk-btn--secondary sk-btn--sm" to={`${base}/crm/activities`}>
                فعالیت‌ها و پیگیری‌ها
              </Link>
            </div>
            <dl className="rep-facts">
              <Fact label="باز" value={faInt(activities.open)} />
              <Fact label="عقب‌افتاده" value={faInt(activities.overdue)} />
              <Fact label="انجام‌شده (در بازه)" value={faInt(cur.activitiesDone)} />
              <Fact label="انجام‌شده (کل)" value={faInt(activities.done)} />
              <Fact label="لغوشده" value={faInt(activities.cancelled)} />
            </dl>
            {Object.keys(activities.byType).length > 0 ? (
              <Distribution
                title="بر اساس نوع"
                total={Object.values(activities.byType).reduce((t, v) => t + v, 0)}
                items={Object.entries(activities.byType).map(([k, v]) => ({
                  label: ACTIVITY_TYPE_LABELS[k] ?? k,
                  value: v,
                }))}
              />
            ) : (
              <V2EmptyState icon="calendar" title="فعالیتی ثبت نشده" compact />
            )}
          </div>
        </V2Card>
      ) : null}

      <V2Modal
        open={analysisOpen}
        title={
          <span className="call-base__modal-title">
            <YarFace size={28} />
            تحلیل عملکرد {member.name}
          </span>
        }
        size="lg"
        onClose={() => setAnalysisOpen(false)}
      >
        <div className="rep-analysis">
          <p className="rep-analysis__summary">{insights.summary}</p>
          <div className="rep-analysis__cols">
            <Findings
              tone="strong"
              title="نقاط قوت"
              items={insights.strengths}
              comparable={insights.comparable}
            />
            <Findings
              tone="weak"
              title="نقاط ضعف"
              items={insights.weaknesses}
              comparable={insights.comparable}
            />
          </div>
          {insights.notes.length > 0 ? (
            <ul className="rep-analysis__notes">
              {insights.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          ) : null}
          <small className="rep-analysis__basis">
            مبنای تحلیل: مقایسهٔ شاخص‌های همین بازه با میانگین {faInt(data.teamSize)} فروشندهٔ فعال
            و خلاصهٔ تحلیل تماس‌ها. این تحلیل محاسبهٔ مستقیم است و اعتبار هوش مصنوعی مصرف نمی‌کند.
          </small>
        </div>
      </V2Modal>
    </main>
  );
}

function shareText(part: string, whole: string): string {
  const w = BigInt(whole || '0');
  if (w <= 0n) return '—';
  return formatPermille(Number((BigInt(part || '0') * 1000n) / w));
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly tone?: 'up' | 'down' | undefined;
}) {
  return (
    <div className="rep-stat">
      <span className="rep-stat__label">{label}</span>
      <strong className="rep-stat__value sk-num">{value}</strong>
      <small className={`rep-stat__sub${tone ? ` is-${tone}` : ''}`}>{sub}</small>
    </div>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className="sk-num">{value}</dd>
    </div>
  );
}

function Distribution({
  title,
  total,
  items,
}: {
  readonly title: string;
  readonly total: number;
  readonly items: readonly { readonly label: string; readonly value: number }[];
}) {
  const visible = items.filter((i) => i.value > 0);
  return (
    <div className="rep-distribution">
      <h3>{title}</h3>
      {visible.length === 0 ? (
        <small>—</small>
      ) : (
        <ul>
          {visible.map((i) => (
            <li key={i.label}>
              <span>{i.label}</span>
              <span className="rep-bar">
                <span
                  className="rep-bar__fill is-average"
                  style={{
                    width: `${Math.max(3, Math.round((i.value * 100) / Math.max(total, 1)))}%`,
                  }}
                />
              </span>
              <b className="sk-num">{faInt(i.value)}</b>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Findings({
  tone,
  title,
  items,
  comparable,
}: {
  readonly tone: 'strong' | 'weak';
  readonly title: string;
  readonly items: readonly {
    readonly metricId: string;
    readonly title: string;
    readonly detail: string;
  }[];
  readonly comparable: boolean;
}) {
  return (
    <section className={`rep-findings is-${tone}`}>
      <h3>{title}</h3>
      {items.length === 0 ? (
        <p>
          {comparable
            ? tone === 'strong'
              ? 'نقطهٔ قوت برجسته‌ای نسبت به تیم مشخص نشد.'
              : 'نقطهٔ ضعف برجسته‌ای نسبت به تیم مشخص نشد.'
            : 'برای مقایسه تیم کوچک است.'}
        </p>
      ) : (
        <ul>
          {items.map((f) => (
            <li key={f.metricId}>
              <b>{f.title}</b>
              <span>{f.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
